<template>
  <div>
    <div v-if="isHome || expandedDevices.length === 0">
      <Button
        variant="fill"
        color="glass"
        size="lg"
        shape="corner"
        :active="activeTab === 'home'"
        @click="handleTab('home')"
      >
        <Icon icon="Status-Fill-Home"></Icon>
        {{ t('home.title') }}
      </Button>
    </div>

    <HoverCard v-else>
      <HoverCardTrigger>
        <Button
          variant="fill"
          color="glass"
          size="lg"
          shape="corner"
          :active="activeTab === 'home'"
          @click="handleTab('home')"
        >
          <Icon icon="Status-Fill-Home"></Icon>
          {{ t('home.title') }}
        </Button>
      </HoverCardTrigger>
      <HoverCardContent align="start" :side-offset="14" class="glass-s30 py-4 w-fit rounded-5xl">
        <div
          ref="scrollRef"
          class="fade-scroll content px-4 flex item-center gap-4 overflow-x-auto overflow-y-hidden min-w-107 max-w-172"
          :style="maskVars"
          @wheel.prevent="handleWheel"
          @scroll="onScroll"
        >
          <div
            v-for="(item, index) in expandedDevices"
            :key="index"
            class="cursor-pointer flex flex-col items-center shrink-0 w-48"
            @click="deviceStore.handleDeviceClick(item)"
          >
            <div class="w-full h-35 p-1 bg-glass30 rounded-3xl">
              <div
                class="w-full h-full flex items-center justify-center relative bg-neutral-light-default1 rounded-3xl overflow-hidden"
              >
                <div
                  v-show="isNowDevice(item)"
                  class="absolute top-1 left-1 flex items-center justify-center w-12 h-6 rounded-4xl glass-blue bg-white text-s-medium"
                >
                  {{ t('home.config.card.current') }}
                </div>
                <img :src="getImageUrl(item.img)" class="w-46 h-33" draggable="false" />
              </div>
            </div>
            <div class="flex flex-col items-center gap-1 mt-4">
              <div class="relative transition-all duration-300 transform text-black-white">
                <Icon :icon="item.icon" class="size-8" />
                <Icon
                  v-if="!item.type?.includes('base')"
                  :icon="item.connectIcon"
                  class="w-4.5 h-4.5 absolute top-0 left-8"
                />
              </div>
              <div class="text-s-medium text-black-white">{{ item.name }}</div>
              <div v-if="!item.type?.includes('base')" class="mt-1 transition-all duration-300">
                <Battery
                  :percentage="item.battery"
                  :charging="item.batteryStatus !== 1"
                  :size="32"
                />
              </div>
            </div>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  </div>
</template>
<script setup lang="ts">
  import { ref, computed, useTemplateRef, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
  import { useRoute } from 'vue-router'
  import { storeToRefs } from 'pinia'
  import { Button, Icon, HoverCard, HoverCardContent, HoverCardTrigger } from '@rapoo/element'
  import { DeviceItem, useDeviceStore, useLocaleStore } from '@/stores'
  import { getImageUrl } from '@/utils/assets'
  import Battery from '@/components/battery/index.vue'

  const { t } = useLocaleStore()
  defineProps({
    activeTab: {
      type: String,
      default: '',
    },
  })
  const emit = defineEmits(['handleTab'])
  const deviceStore = useDeviceStore()
  const { expandedDevices, currentDevice } = storeToRefs(deviceStore)

  const route = useRoute()
  const isHome = computed(() => route.name === 'home')

  type ScrollState = {
    frameId: number | null
    targetLeft: number
  }

  const scrollStateMap = new WeakMap<HTMLElement, ScrollState>()

  const THRESHOLD = 3

  const scrollContainer = useTemplateRef('scrollRef')
  const atStart = ref(true)
  const atEnd = ref(true)

  let resizeObserver: ResizeObserver | null = null

  const maskVars = computed(() => ({
    '--fade-alpha-left': atStart.value ? 0 : 1,
    '--fade-alpha-right': atEnd.value ? 0 : 1,
  }))

  const handleTab = (key: string) => {
    emit('handleTab', key)
  }

  function checkFade() {
    const el = scrollContainer.value
    if (!el) return

    const maxScroll = el.scrollWidth - el.clientWidth

    // 内容未溢出
    if (maxScroll <= THRESHOLD) {
      atStart.value = true
      atEnd.value = true
      return
    }

    atStart.value = el.scrollLeft <= THRESHOLD
    atEnd.value = maxScroll - el.scrollLeft <= THRESHOLD
  }

  function onScroll() {
    checkFade()
  }

  function bindResizeObserver() {
    const el = scrollContainer.value
    if (!el) return

    resizeObserver?.disconnect()
    resizeObserver = new ResizeObserver(() => {
      checkFade()
    })
    resizeObserver.observe(el)
  }

  onMounted(async () => {
    await nextTick()
    checkFade()
    bindResizeObserver()
  })

  onBeforeUnmount(() => {
    resizeObserver?.disconnect()
    resizeObserver = null
  })

  watch(
    () => expandedDevices.value.length,
    async () => {
      await nextTick()
      checkFade()
      bindResizeObserver()
    },
  )

  watch(scrollContainer, async (el) => {
    if (!el) return
    await nextTick()
    checkFade()
    bindResizeObserver()
  })

  const animateScroll = (container: HTMLElement) => {
    const state = scrollStateMap.get(container)

    if (!state) return

    const distance = state.targetLeft - container.scrollLeft

    if (Math.abs(distance) < 0.5) {
      container.scrollLeft = state.targetLeft
      state.frameId = null
      return
    }

    container.scrollLeft += distance * 0.18
    state.frameId = requestAnimationFrame(() => animateScroll(container))
  }

  const handleWheel = (e: WheelEvent) => {
    const container = e.currentTarget as HTMLElement | null

    if (!container) return

    const delta = e.deltaY || e.deltaX

    if (delta === 0) return

    const maxScrollLeft = container.scrollWidth - container.clientWidth

    if (maxScrollLeft <= 0) return

    const currentTarget = scrollStateMap.get(container)?.targetLeft ?? container.scrollLeft
    const nextTarget = Math.min(Math.max(currentTarget + delta, 0), maxScrollLeft)

    let state = scrollStateMap.get(container)

    if (!state) {
      state = {
        frameId: null,
        targetLeft: nextTarget,
      }
      scrollStateMap.set(container, state)
    } else {
      state.targetLeft = nextTarget
    }

    if (state.frameId === null) {
      state.frameId = requestAnimationFrame(() => animateScroll(container))
    }
  }

  const isNowDevice = (item: DeviceItem) => {
    return item.id === currentDevice.value?.id
  }
</script>

<style scoped>
  @property --fade-alpha-left {
    syntax: '<number>';
    inherits: false;
    initial-value: 0;
  }

  @property --fade-alpha-right {
    syntax: '<number>';
    inherits: false;
    initial-value: 0;
  }

  .content::-webkit-scrollbar {
    display: none;
  }

  .fade-scroll {
    --fade-alpha-left: 0;
    --fade-alpha-right: 0;
    -webkit-mask-image: linear-gradient(
      to right,
      rgba(0, 0, 0, calc(1 - var(--fade-alpha-left))) 0,
      #000 64px,
      #000 calc(100% - 64px),
      rgba(0, 0, 0, calc(1 - var(--fade-alpha-right))) 100%
    );
    mask-image: linear-gradient(
      to right,
      rgba(0, 0, 0, calc(1 - var(--fade-alpha-left))) 0,
      #000 64px,
      #000 calc(100% - 64px),
      rgba(0, 0, 0, calc(1 - var(--fade-alpha-right))) 100%
    );
    transition:
      --fade-alpha-left 0.3s ease,
      --fade-alpha-right 0.3s ease;
  }
</style>
